import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { InView } from 'react-intersection-observer'
import { CaptionLG, ParagraphSM } from '@/atoms/Text'
import {
  GUILD_BASIC_MONTHLY_STANDARD_PRICE,
  GUILD_BASIC_YEARLY_STANDARD_PRICE,
  GUILD_PREMIUM_MONTHLY_STANDARD_PRICE,
  GUILD_PREMIUM_YEARLY_STANDARD_PRICE,
} from '@/constants/guild'
import {
  ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID,
  ANGEL_GUILD_BASIC_YEARLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID,
} from '@/constants/offer-ids'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildPlanBoxTiersStacked } from '@/views/GuildJoinView/GuildPlanSelectionTiersStacked/GuildPlanBoxTiersStacked'

interface Props {
  className?: string
  containerRef?: React.Ref<HTMLDivElement>
  darkMode?: boolean
  signupLayout?: boolean
  hidePremium?: boolean
  hideBasic?: boolean
  hideLabels?: boolean
  hideYearly?: boolean
  disclosePriceInUSD?: boolean
  separateTierPlan?: boolean
  isSignupLayout?: boolean
}

export const GuildPlanSelectionTiersStacked: React.FC<Props> = ({
  className,
  containerRef,
  darkMode = false,
  signupLayout = false,
  hideBasic = false,
  hidePremium = false,
  hideLabels = false,
  hideYearly = false,
  disclosePriceInUSD = false,
  separateTierPlan = false,
  isSignupLayout = false,
}) => {
  const { t } = useTranslate('guild')
  const { query } = useSafeRouter()

  const { trackProductViewed } = useEcommerceEvents()

  return (
    <InView
      triggerOnce
      onChange={(inView) => {
        if (!inView) return
        const projectSlug = query.slug && String(query.slug)

        trackProductViewed({
          category: 'guild',
          funnel: 'guild',
          name: 'Angel Guild Plans',
          price: 0,
          currency: 'USD',
          quantity: 1,
          product_id: 'Angel Guild Plans',
          sku: 'Angel Guild Plans',
          projectSlug,
          variant: 'basic monthly, basic yearly, premium monthly, premium yearly',
        })
      }}
      className="mx-auto flex flex-col items-center"
    >
      <When condition={!hideLabels}>
        {!signupLayout && (
          <CaptionLG
            color={darkMode ? 'white' : 'core-gray-950'}
            weight={signupLayout ? 'semibold' : 'bold'}
            className={classNames('pb-4', { '!photon-title-md': signupLayout })}
          >
            {t('selectMembershipPlans', 'Select membership plans')}.
          </CaptionLG>
        )}
      </When>

      <When condition={disclosePriceInUSD}>
        <ParagraphSM weight="semibold" color="core-gray-800" className="md:pb-4 lg:-mt-4">
          *{t('pricesInUsd', 'Prices in USD')}
        </ParagraphSM>
      </When>
      <div
        ref={containerRef}
        className={classNames(
          'mx-4 grid grid-cols-1 gap-8 *:col-span-3 lg:*:col-span-1 pb-8',
          hidePremium ? 'lg:col-span-1' : 'lg:grid-cols-2',
          className,
        )}
      >
        <When condition={!hidePremium}>
          <PremiumPlan
            orderClass={separateTierPlan ? 'lg-order-0' : 'lg:order-2'}
            title={t('premium', 'Premium')}
            hideYearly={hideYearly}
            separateTierPlan={separateTierPlan}
            isSignupLayout={isSignupLayout}
          />
        </When>
        <When condition={!hideBasic}>
          <BasicPlan
            orderClass={separateTierPlan ? 'lg-order-2' : 'lg:order-0'}
            title={t('basic', 'Basic')}
            hidePremiumBenefits={hidePremium}
            hideYearly={hideYearly}
            disclosePriceInUSD={disclosePriceInUSD}
            separateTierPlan={separateTierPlan}
            isSignupLayout={isSignupLayout}
          />
        </When>
      </div>
    </InView>
  )
}

interface Plan {
  className?: string
  orderClass?: string
  planInterval?: 'annual' | 'monthly'
  title: string
  isQuote?: boolean
  hidePremiumBenefits?: boolean
  hideYearly?: boolean
  disclosePriceInUSD?: boolean
  separateTierPlan?: boolean
  isSignupLayout?: boolean
}

export const PremiumPlan: React.FC<Plan> = ({
  className,
  orderClass,
  planInterval,
  title,
  hideYearly,
  disclosePriceInUSD,
  separateTierPlan,
  isSignupLayout,
}) => {
  return (
    <GuildPlanBoxTiersStacked
      hideYearly={hideYearly}
      className={className}
      offerId={ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID}
      monthlyOfferId={ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID}
      orderClass={orderClass}
      price={GUILD_PREMIUM_YEARLY_STANDARD_PRICE}
      monthlyPrice={GUILD_PREMIUM_MONTHLY_STANDARD_PRICE}
      tier="premium"
      title={title}
      planInterval={planInterval}
      punchy
      punchyButtonColors
      disclosePriceInUSD={disclosePriceInUSD}
      separateTierPlan={separateTierPlan}
      isSignupLayout={isSignupLayout}
    />
  )
}

export const BasicPlan: React.FC<Plan> = ({
  className,
  orderClass,
  planInterval,
  title,
  hideYearly,
  disclosePriceInUSD,
  separateTierPlan,
  isSignupLayout,
}) => {
  return (
    <GuildPlanBoxTiersStacked
      hideYearly={hideYearly}
      className={className}
      offerId={ANGEL_GUILD_BASIC_YEARLY_OFFER_ID}
      monthlyOfferId={ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID}
      price={GUILD_BASIC_YEARLY_STANDARD_PRICE}
      monthlyPrice={GUILD_BASIC_MONTHLY_STANDARD_PRICE}
      orderClass={orderClass}
      tier="basic"
      planInterval={planInterval}
      title={title}
      disclosePriceInUSD={disclosePriceInUSD}
      separateTierPlan={separateTierPlan}
      isSignupLayout={isSignupLayout}
      punchyButtonColors
    />
  )
}
